import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader/Loader";
import SlidePanel from "../../../../components/SlidePanel/SlidePanel";
import * as workflowService from "../../../../services/workflowService";

// Which services use a workflow. The simple case is a tick per service category
// (every service type under it follows). "Customise by service type" opens a
// category's types so one can follow a different rule: use this workflow, use none,
// or go back to following the category.
//
// Offering states, against what is stored today:
//   follow  no row of its own - it follows its category
//   this    its own row pointing at this workflow
//   none    its own row with no workflow (overrides the category)
//   other   its own row pointing at a different workflow (shown, left alone unless changed)
const keyOf = (scope, id) => `${scope}:${id}`;

function currentState(links, templateId, offeringId) {
  const row = links.find((l) => l.scope === "OFFERING" && Number(l.scope_id) === Number(offeringId));
  if (!row) return "follow";
  if (row.template_id === null || row.template_id === undefined) return "none";
  return Number(row.template_id) === Number(templateId) ? "this" : "other";
}

function CategoryRow({ category, checked, usedBy, expanded, offerings, choices, links, templateId, templateNames, onToggle, onExpand, onChoice }) {
  return (
    <div className="mb-2" style={{ border: "1px solid #E5E7EB", borderRadius: "10px", padding: "8px 12px" }}>
      <Form.Check
        type="checkbox"
        id={`wf-cat-${category.id}`}
        label={<strong>{category.name}</strong>}
        checked={checked}
        onChange={onToggle}
      />
      {usedBy && !checked && (
        <div style={{ fontSize: "12px", color: "#B45309", marginLeft: "24px" }}>
          Currently uses "{usedBy}". Ticking this switches it to this workflow.
        </div>
      )}
      <button
        type="button"
        className="btn btn-link btn-sm p-0 mt-1"
        style={{ marginLeft: "24px", fontSize: "12px" }}
        onClick={onExpand}
        aria-expanded={expanded}
      >
        {expanded ? "Hide service types" : "Customise by service type"}
      </button>
      {expanded && (
        <div className="mt-2" style={{ marginLeft: "24px" }}>
          {offerings === undefined ? (
            <Skeleton count={2} height={28} />
          ) : offerings.length === 0 ? (
            <div style={{ fontSize: "12px", color: "#6B7280" }}>No service types yet.</div>
          ) : (
            offerings.map((o) => {
              const state = choices[o.id] ?? currentState(links, templateId, o.id);
              const otherRow = links.find((l) => l.scope === "OFFERING" && Number(l.scope_id) === Number(o.id) && l.template_id && Number(l.template_id) !== Number(templateId));
              return (
                <div key={o.id} className="d-flex align-items-center justify-content-between mb-1" style={{ fontSize: "13px" }}>
                  <span>{o.name}</span>
                  <Form.Select
                    size="sm"
                    style={{ width: "auto" }}
                    value={state}
                    aria-label={`${o.name} workflow`}
                    onChange={(e) => onChoice(o.id, e.target.value)}
                  >
                    <option value="follow">Same as {category.name}</option>
                    <option value="this">This workflow</option>
                    <option value="none">No workflow</option>
                    {otherRow && <option value="other">Uses "{templateNames[otherRow.template_id] || "another workflow"}"</option>}
                  </Form.Select>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default function WorkflowServicesPanel({ show, template, categories, links, templateNames, onHide, onSaved }) {
  const [ticked, setTicked] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [offeringsByCat, setOfferingsByCat] = useState({});
  const [choices, setChoices] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const templateId = template?.id;

  useEffect(() => {
    if (!show || !templateId) return;
    setTicked(categories.filter((c) => links.some((l) => l.scope === "CATEGORY" && Number(l.scope_id) === Number(c.id) && Number(l.template_id) === Number(templateId))).map((c) => c.id));
    setExpanded({});
    setOfferingsByCat({});
    setChoices({});
    setError("");
  }, [show, templateId, categories, links]);

  const categoryUse = (categoryId) => {
    const row = links.find((l) => l.scope === "CATEGORY" && Number(l.scope_id) === Number(categoryId));
    return row && Number(row.template_id) !== Number(templateId) ? templateNames[row.template_id] : null;
  };

  const toggleCategory = (id) => setTicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const expand = async (categoryId) => {
    const open = !expanded[categoryId];
    setExpanded((prev) => ({ ...prev, [categoryId]: open }));
    if (open && offeringsByCat[categoryId] === undefined) {
      try {
        const res = await workflowService.getCategoryOfferings(categoryId);
        setOfferingsByCat((prev) => ({ ...prev, [categoryId]: res?.success !== false && Array.isArray(res?.data) ? res.data.filter((o) => o.is_active !== 0).map((o) => ({ id: o.id, name: o.name })) : [] }));
      } catch (e) {
        setOfferingsByCat((prev) => ({ ...prev, [categoryId]: [] }));
      }
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      // Offerings already on this workflow stay unless an opened category changed them,
      // so closing a category never silently unlinks its types.
      const loaded = Object.values(offeringsByCat).flat();
      const stillThis = new Set(links.filter((l) => l.scope === "OFFERING" && Number(l.template_id) === Number(templateId)).map((l) => Number(l.scope_id)));
      for (const o of loaded) {
        const state = choices[o.id] ?? currentState(links, templateId, o.id);
        if (state === "this") stillThis.add(Number(o.id));
        else stillThis.delete(Number(o.id));
      }

      const res = await workflowService.setWorkflowTemplateLinks(templateId, { categoryIds: ticked, offeringIds: [...stillThis] });
      if (!res?.success) {
        setError(workflowService.resultMessage(res));
        return;
      }
      // The rest of the per-type choices that "this workflow" does not cover.
      for (const o of loaded) {
        const now = currentState(links, templateId, o.id);
        const want = choices[o.id] ?? now;
        if (want === now || want === "this" || want === "other") continue;
        const r = want === "none"
          ? await workflowService.setWorkflowLink({ scope: "OFFERING", scope_id: o.id, template_id: null })
          : await workflowService.setWorkflowLink({ scope: "OFFERING", scope_id: o.id, clear: true });
        if (!r?.success) {
          setError(workflowService.resultMessage(r));
          return;
        }
      }
      toast.success("Services updated");
      onSaved();
    } catch (e) {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={onHide}
      title={`Use "${template?.name || ""}" for`}
      width={560}
      footer={
        <>
          <button className="btn btn-outline-secondary px-4" onClick={onHide}>
            Cancel
          </button>
          <button className="btn Button primaryBackground text-white px-4" onClick={save} disabled={saving}>
            {saving ? <Loader style={{ width: 40, height: 20 }} /> : "Save"}
          </button>
        </>
      }
    >
      <p style={{ fontSize: "13px", color: "#6B7280" }}>
        Tick the service categories that use this workflow. New bookings for them follow its steps; bookings already
        made are not changed.
      </p>
      {categories.length === 0 ? (
        <div style={{ fontSize: "13px", color: "#6B7280" }}>You have no service categories yet. Add one in Setup first.</div>
      ) : (
        categories.map((c) => (
          <CategoryRow
            key={c.id}
            category={c}
            checked={ticked.includes(c.id)}
            usedBy={categoryUse(c.id)}
            expanded={!!expanded[c.id]}
            offerings={offeringsByCat[c.id]}
            choices={choices}
            links={links}
            templateId={templateId}
            templateNames={templateNames}
            onToggle={() => toggleCategory(c.id)}
            onExpand={() => expand(c.id)}
            onChoice={(offeringId, value) => setChoices((prev) => ({ ...prev, [offeringId]: value }))}
          />
        ))
      )}
      {error && (
        <div className="text-danger mt-2" style={{ fontSize: "13px" }} role="alert">
          {error}
        </div>
      )}
    </SlidePanel>
  );
}
